import React from 'react';
import { Tooltip } from 'antd';
import { explainReferenceReason } from '../../../utils/pairPnlDisplay';
import { formatInt } from '../../../utils/numberFormat';

/**
 * The v3 OPEN trigger, as one headline number.
 *
 * The trigger has NO threshold band: a negative value means the bot opens a pair
 * as soon as it is running and un-paused. That is the whole reason this is on
 * screen, so the warning below the number is the point, not decoration.
 *
 * Three states, all of which have to be visible:
 *   - a number, per pair (one contract). `engine` is the bot's own published
 *     breakdown; `preview` is the browser's copy of the same rules applied to
 *     whatever is in the form right now. The preview is what makes the
 *     reference-price fields usable at all — before Start, and while editing a
 *     running pair. Which of the two it is sits in the label's tooltip: on screen
 *     it only earns a line when it misleads — a stale snapshot, or edits the bot
 *     has not been given.
 *   - UNDEFINED — the engine refuses to act (stale reference, missing quote).
 *     Silence here used to look identical to "no signal", which hid exactly the
 *     post-roll state an operator needs to fix.
 *   - defined and negative, but something held it: `blockedReason`.
 */
export interface ReferencePnlBannerProps {
    /** Raw VND, or null when the trigger is undefined. */
    total: number | null;
    source: 'engine' | 'preview';
    undefinedReason: string | null;
    blockedReason: string | null;
    /** Seconds since the engine wrote the snapshot; null for the preview. */
    ageSeconds: number | null;
    /**
     * Preview shown because the trigger inputs carry unsaved edits, not because
     * the bot is stopped. The bot is still acting on the SAVED reference, so the
     * number on screen is not what it is trading on yet.
     */
    unsavedEdits?: boolean;
    /**
     * Something is holding one of the inputs still (today: an expired futures
     * contract). Shown because a frozen number and a quiet market look the same.
     */
    frozenNote?: string | null;
    /** Config's `is_pause` — suppresses the "will open immediately" warning. */
    isPause: boolean;
    /** Smaller type for the inline card (vs the Start modal). */
    compact?: boolean;
}

const RED = '#F6465D';
const GREEN = '#0ECB81';
const GREY = '#848E9C';

// Older than this and the engine has effectively stopped reporting between
// polls — worth saying, because the number stops tracking the market.
const STALE_AFTER_SEC = 30;

const ReferencePnlBanner: React.FC<ReferencePnlBannerProps> = ({
    total,
    source,
    undefinedReason,
    blockedReason,
    ageSeconds,
    unsavedEdits = false,
    frozenNote,
    isPause,
    compact = false,
}) => {
    const reason = explainReferenceReason(undefinedReason);
    const blocked = explainReferenceReason(blockedReason);
    if (total === null && !reason && !frozenNote) return null;

    const negative = total !== null && total < 0;
    const accent = total === null ? GREY : negative ? RED : GREEN;
    const stale = ageSeconds != null && ageSeconds > STALE_AFTER_SEC;

    return (
        <div
            style={{
                margin: compact ? '4px 0 8px' : undefined,
                marginBottom: compact ? undefined : 12,
                padding: '8px 10px',
                borderRadius: 4,
                fontSize: compact ? 11 : 12,
                background:
                    total === null
                        ? 'rgba(132,142,156,0.10)'
                        : negative
                          ? 'rgba(246,70,93,0.12)'
                          : 'rgba(14,203,129,0.10)',
                border: `1px solid ${accent}`,
                color: '#EAECEF',
            }}
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                {/* One label, and everything it used to spell out on screen — one
                    contract, live prices, where the number came from — in its tooltip.
                    The sub-line below is now kept for the two states that change what
                    the number means, not for the steady state. */}
                <Tooltip
                    title={
                        `PnL of ONE pair (1 contract) opened now at your reference prices, marked to the live market. ` +
                        `This is the OPEN trigger itself — not a position, and not scaled by allocated cash. ` +
                        (source === 'engine'
                            ? `Published by the running bot${ageSeconds != null ? `, ${ageSeconds}s ago` : ''}.`
                            : 'Computed in the browser with the same rules — no bot is reporting.')
                    }
                >
                    <span style={{ color: GREY, cursor: 'help' }}>Reference PnL per pair</span>
                </Tooltip>
                <strong style={{ color: accent, whiteSpace: 'nowrap' }}>
                    {total === null
                        ? 'UNDEFINED'
                        : `${formatInt(total)} VND`}
                </strong>
            </div>

            {/* Only the two states where the headline is not what the bot is acting on:
                a stale engine snapshot, and a preview of edits it has not been given.
                "from the bot" and "preview · bot not reporting" said nothing an operator
                had to act on, so they moved into the label's tooltip. */}
            {source === 'engine' && stale && (
                <div style={{ marginTop: 4, color: GREY, fontSize: compact ? 10 : 11 }}>
                    <Tooltip title="The bot publishes this on every futures tick. It has not for a while, so the number has stopped tracking the market.">
                        <span style={{ cursor: 'help' }}>stale · {ageSeconds}s ago</span>
                    </Tooltip>
                </div>
            )}
            {source === 'preview' && unsavedEdits && (
                <div style={{ marginTop: 4, fontSize: compact ? 10 : 11 }}>
                    <Tooltip title="Computed from the values currently in this form. The bot is running and still trading on the SAVED reference — Save to hand it these values.">
                        <span style={{ cursor: 'help', color: '#F0B90B' }}>
                            unsaved edits · bot still on the saved reference
                        </span>
                    </Tooltip>
                </div>
            )}

            {frozenNote && (
                <div style={{ marginTop: 6, color: RED }}>⚠️ {frozenNote}</div>
            )}

            {total === null && reason && (
                <div style={{ marginTop: 6, color: GREY }}>
                    ⓘ The bot will NOT open: {reason}.
                </div>
            )}

            {negative && blocked && (
                <div style={{ marginTop: 6, color: GREY }}>
                    ⓘ Negative, but nothing is opening: {blocked}.
                </div>
            )}

            {negative && !blocked && !isPause && (
                <div style={{ marginTop: 6, color: RED }}>
                    ⚠️ Negative → the bot opens a pair immediately{source === 'preview' ? ' on start' : ''}.
                    Pause opening or adjust reference prices if that's not intended.
                </div>
            )}
        </div>
    );
};

export default ReferencePnlBanner;
